import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import { getFranceCitiesByDepartmentList } from '../../../../api/countries/get-france-cities-by-department'
import { getFranceDepartmentsList } from '../../../../api/countries/get-france-departments-list'
import { getNationalitiesList } from '../../../../api/countries/get-nationalities-list'
import { checkSsnFormatValidity } from '../../../../api/staff-application/check-ssn-format-validity'
import useTranslate from '../../../../lib/hooks/use-translate'
import { GenderEnum, genderOptionsMapping } from '../../../../lib/types/enums/common/gender.enum'
import {
  AcademicZoneEnum,
  academicZoneEnumMapping
} from '../../../../lib/types/enums/staff/academic-zone.enum'
import { isStaffEnglish } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  MaritalStatusEnum,
  maritalStatusEnumMapping
} from '../../../../lib/types/enums/staff-application/marital-status.enum'
import { CountryKey, getCountryLabel } from '../../../../lib/types/models/country/country-list'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import {
  extractErrorMessage,
  formatSsn,
  getSelectedEnumOption,
  getSelectedOption
} from '../../../../lib/utils/functions/misc.functions'
import ControlledSwitch from '../../../elements/controlled-switch'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { getStaffAcademicZone } from '../utils/get-academic-zone'

const StaffPersonalInformationForm = () => {
  const [debouncedSsn, setDebouncedSsn] = useState('')
  const queryClient = useQueryClient()
  const { t, currentLanguage } = useTranslate()
  const form = useFormContext<AdminStaffInput>()

  const { birthPlace, birthDepartment, postalCode, country, socialSecurityNumber } = form.watch()
  const isBirthPlaceFrance = birthPlace === CountryKey.FRA
  const isEnglishStaff = isStaffEnglish({ qualifications: form.watch('qualifications') })

  // The academic zone is derived from the residence postal code and country (the backend recomputes it too)
  useEffect(() => {
    form.setValue('academicZone', getStaffAcademicZone(postalCode, country), {
      shouldValidate: true
    })
  }, [postalCode, country])

  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const { data: franceDepartmentsList } = useQuery({
    queryKey: ['france-departments-list'],
    queryFn: getFranceDepartmentsList
  })

  const { data: franceCitiesByDepartment } = useQuery({
    queryKey: ['france-cities-by-department-list'],
    queryFn: () => {
      if (birthDepartment) return getFranceCitiesByDepartmentList(birthDepartment)
    },
    enabled: isBirthPlaceFrance
  })

  const { mutateAsync: checkSsnFormatValidityMutation } = useMutation({
    mutationFn: checkSsnFormatValidity
  })

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!debouncedSsn) return

      try {
        const ssn = await checkSsnFormatValidityMutation(debouncedSsn)

        if (ssn) {
          // If SSN is valid, update the form value
          form.setValue('socialSecurityNumber', ssn, { shouldValidate: true })
        }
      } catch (error) {
        const err = error as Error
        const message = extractErrorMessage(err)
        // If there's an error (invalid SSN), set the form error
        if (message)
          form.setError('socialSecurityNumber', {
            type: 'manual',
            message: message
          })
      }
    }, 500) // Adjust debounce delay as needed

    return () => clearTimeout(handler)
  }, [debouncedSsn])

  //whenever birthDepartment changes, we need to update the cities list
  useEffect(() => {
    if (isBirthPlaceFrance && birthDepartment) {
      queryClient.invalidateQueries({ queryKey: ['france-cities-by-department-list'] })
    }
  }, [birthDepartment])

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Genre : *
      </Typography>
      <div className="flex gap-2">
        {Object.values(GenderEnum).map((gender) => (
          <FormField
            key={gender}
            control={form.control}
            name="gender"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <ControlledRadioInput
                      {...field}
                      label={genderOptionsMapping[gender].title}
                      checked={field.value === gender}
                      onCheck={() => field.onChange(gender)}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        ))}
      </div>
      <div className="flex gap-3 items-center w-full">
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="NOM" type="text" required />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Prénom" type="text" required />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="maidenName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="NOM de jeune fille" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="w-full flex items-center gap-3">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Email" type="email" required />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledPhoneInput
                  {...field}
                  label="Numéro de téléphone"
                  defaultCountry="FR"
                  required
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="w-full flex items-center gap-3">
        <FormField
          control={form.control}
          name="address1"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  label="Adresse"
                  required
                  onDetailsChange={(placeDetails) => {
                    form.setValue('address1', placeDetails.address, { shouldValidate: true })
                    if (placeDetails.postalCode || !form.getValues().postalCode)
                      form.setValue('postalCode', placeDetails.postalCode, {
                        shouldValidate: true
                      })
                    if (placeDetails.city || !form.getValues().city)
                      form.setValue('city', placeDetails.city, { shouldValidate: true })
                    if (placeDetails.country || !form.getValues().country)
                      form.setValue('country', placeDetails.country, { shouldValidate: true })
                    form.trigger(['address1', 'postalCode', 'city', 'country'])
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address2"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Complément d'adresse" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="postalCode"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Code Postal" type="text" required />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Ville" type="text" required />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Pays" type="text" required />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="academicZone"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required={!isEnglishStaff}
                  label="Zone Académique"
                  options={Object.values(AcademicZoneEnum).map((status) => ({
                    label: academicZoneEnumMapping[status],
                    value: status
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, academicZoneEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="flex  gap-2 ">
        <div className="flex gap-2 items-center w-full">
          <FormField
            control={form.control}
            name="birthDate"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    type="birthdate"
                    label="Date de naissance"
                    required
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Badge
            size="medium"
            className="w-fit flex items-center justify-center cursor-not-allowed whitespace-nowrap"
            text={`Âge : ${form.getValues().birthDate ? DateFormatter.calculateAge(form.getValues().birthDate) + ' ans' : '-'}`}
            variant="grey"
          />
        </div>

        <FormField
          control={form.control}
          name="birthPlace"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput<CountryKey>
                  {...field}
                  searchable
                  label={'Pays de naissance'}
                  options={countryList?.data.map((country) => ({
                    label: getCountryLabel(currentLanguage, country) ?? '',
                    value: country.key as CountryKey
                  }))}
                  onChange={(value) => {
                    form.setValue('birthPlace', value, { shouldValidate: true })
                    if (value !== CountryKey.FRA)
                      form.setValue('birthDepartment', null, { shouldValidate: true })
                  }}
                  selectedOption={conditionalRender(!!field?.value, {
                    value: field.value,
                    label:
                      getCountryLabel(
                        currentLanguage,
                        countryList?.data.find(
                          (country) => (country.key as CountryKey) === field.value
                        )
                      ) || ''
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2">
        {isBirthPlaceFrance && (
          <FormField
            control={form.control}
            name="birthDepartment"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    // the backend refuses the save without it once a social security number is entered (mandatory unless english staff)
                    required={!isEnglishStaff || !!socialSecurityNumber}
                    label="Département de naissance"
                    options={franceDepartmentsList?.map((department) => ({
                      label: department,
                      value: department
                    }))}
                    selectedOption={getSelectedOption(field.value, field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
        {isBirthPlaceFrance ? (
          <FormField
            control={form.control}
            name="birthCity"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    label="Ville de naissance"
                    options={franceCitiesByDepartment?.map((department) => ({
                      label: department,
                      value: department
                    }))}
                    selectedOption={getSelectedOption(field.value, field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="birthCity"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} label="Ville de naissance" type="text" />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="socialSecurityNumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label="N° de sécurité sociale"
                  type="text"
                  required={!isEnglishStaff}
                  value={field.value ? formatSsn(field.value) : ''}
                  maxLength={21}
                  onChange={(event) => {
                    const value = typeof event === 'string' ? event : event?.target?.value || ''

                    // Format SSN before passing it to the field's onChange
                    const formattedValue = formatSsn(value)
                    // Update field value and set debounced value
                    field.onChange(formattedValue)
                    setDebouncedSsn(formattedValue) // Set the debounced value for later use
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="nationality"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label="Nationalité"
                  searchable
                  required
                  options={nationalitiesList?.map((nationality) => ({
                    label: nationality.label,
                    value: nationality.code
                  }))}
                  selectedOption={getSelectedOption(
                    field.value,
                    nationalitiesList?.find((national) => national.code === field.value)?.label
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="maritalStatus"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label="Situation de famille"
                  options={Object.values(MaritalStatusEnum).map((status) => ({
                    label: t(maritalStatusEnumMapping[status]),
                    value: status
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, maritalStatusEnumMapping, t)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="numberOfChildren"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  type="number"
                  isControllable
                  allowNegative={false}
                  label="Nombre d'enfants"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="allergies"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledInput {...field} label="Allergies" type="text" />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="partOfTheEuropeanUnion"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledSwitch {...field} label="Membre de l'Union Européenne ?" withBorder />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}

export default StaffPersonalInformationForm
